import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import I18nText from "../../../components/I18nText/I18nText";
import { Button } from "../../../components/elements";
import { ICategoryList } from "../types";
import "./CategoryList.scss";
import "./CategoryList_flint.scss";
import "./CategoryList_kokoro.scss";
import "./CategoryList_pdd.scss";
import "./CategoryList_shabuDays.scss";
import "./CategoryList_torikizoku.scss";
import "./CategoryList_wang.scss";

interface ICategoryListProps {
	categories: ICategoryList[];
	activeCategoryId: string | null;
	onCategoryClick: (categoryId: string) => void;
}

const CategoryList: React.FC<ICategoryListProps> = ({
	categories,
	activeCategoryId,
	onCategoryClick,
}) => {
	const categoryListRef = useRef<HTMLDivElement>(null);
	const categoryItemRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
	const [isInitialized, setIsInitialized] = useState(false);

	// Initial positioning without animation
	useEffect(() => {
		if (activeCategoryId && categories.length > 0 && !isInitialized) {
			const activeButton = categoryItemRefs.current.get(activeCategoryId);

			if (activeButton) {
				// Use instant positioning for initial load
				activeButton.scrollIntoView({
					behavior: "instant",
					block: "nearest",
					inline: "center",
				});
				setIsInitialized(true);
			} else {
				// If button not found yet, try again after a short delay
				const timer = setTimeout(() => {
					const delayedActiveButton = categoryItemRefs.current.get(activeCategoryId);

					if (delayedActiveButton) {
						delayedActiveButton.scrollIntoView({
							behavior: "instant",
							block: "nearest",
							inline: "center",
						});
						setIsInitialized(true);
					}
				}, 100);

				return () => clearTimeout(timer);
			}
		}
	}, [categories, activeCategoryId, isInitialized]);

	// Smooth scrolling for subsequent changes
	useEffect(() => {
		if (activeCategoryId && isInitialized) {
			const activeButton = categoryItemRefs.current.get(activeCategoryId);

			if (activeButton) {
				activeButton.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
					inline: "center",
				});
			}
		}
	}, [activeCategoryId, isInitialized]);
	const handleCategoryClick = useCallback(
		(categoryId: string) => () => onCategoryClick(categoryId),
		[onCategoryClick]
	);

	return (
		<div ref={categoryListRef} className="CategoryListContainer">
			{categories.map((category) => (
				<Button
					data-testid={`category-button-${category.id}`}
					key={category.id}
					ref={(el) => categoryItemRefs.current.set(category.id, el)}
					className={`my-custom-button CategoryItem ${category.id === activeCategoryId ? "Active" : ""}`}
					onClick={handleCategoryClick(category.id)}
				>
					<I18nText enText={category.name} cnText={category.name_cn} />
				</Button>
			))}
		</div>
	);
};

export default memo(CategoryList);
